// BMP-HOOK-START:cart-upsell
// "Pairs great with" cart drawer carousel (one-tap add per card). Delegated, loaded once (Section Rendering morphs never re-run scripts).
// On tap: fresh /cart.js + /products/<handle>.js, pick the variant (same size, else nearest in XS..5XL,
// ties -> larger, same color only), add without blocking, refresh the drawer through the theme's
// standard CartLinesUpdateEvent flow, and show a short fading note when a different size was used.
import { CartLinesUpdateEvent } from '@shopify/events';

const ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
const NOTE_MS = 4000;
const root = () => window.Shopify?.routes?.root || '/';

function refSizeFrom(cart) {
  // /cart.js items are newest-first.
  for (const item of cart.items || []) {
    const opt = (item.options_with_values || []).find((o) => o.name === 'Size');
    if (opt) return String(opt.value).toUpperCase();
  }
  return null;
}

export function pickVariant(product, color, refSize) {
  const ci = product.options.findIndex((o) => (o.name || o) === 'Color');
  const si = product.options.findIndex((o) => (o.name || o) === 'Size');
  const pool = product.variants.filter((v) => v.available && (ci < 0 || v.options[ci] === color));
  if (!pool.length) return null;
  const refIdx = refSize ? ORDER.indexOf(refSize) : -1;
  if (si < 0 || refIdx < 0) return { variant: pool[0], fallback: false };
  let best = null;
  let bestScore = Infinity;
  for (const v of pool) {
    const idx = ORDER.indexOf(String(v.options[si]).toUpperCase());
    if (idx < 0) continue;
    const score = Math.abs(idx - refIdx) * 100 - idx; // nearest first, tie -> larger
    if (score < bestScore) { bestScore = score; best = v; }
  }
  if (!best) return null;
  return { variant: best, fallback: String(best.options[si]).toUpperCase() !== refSize, size: best.options[si] };
}

let pendingNote = null;

function revealNote(el) {
  // The drawer's item list scrolls; the note is the last thing in it, so scroll the list to the end.
  const scroller = el.closest('.cart-drawer__items');
  if (!scroller || scroller.scrollHeight <= scroller.clientHeight) return;
  const r = el.getBoundingClientRect();
  const s = scroller.getBoundingClientRect();
  if (r.bottom > s.bottom - 8 || r.top < s.top) scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
}

let revealQueued = false;

function placeNote() {
  if (!pendingNote) return;
  const { text, until } = pendingNote;
  if (Date.now() > until) { pendingNote = null; return; }
  const el = document.querySelector('#cart-drawer [data-bmp-cu-note]');
  if (!el) return;
  if (el.textContent !== text) {
    el.textContent = text;
    el.classList.remove('is-fading');
    const left = until - Date.now();
    setTimeout(() => el.classList.add('is-fading'), Math.max(0, left - 600));
    setTimeout(() => { if (el.textContent === text) el.textContent = ''; }, left);
  }
  // The new cart line lands above the note during the morph; keep the note in view while it shows.
  if (!revealQueued) {
    revealQueued = true;
    requestAnimationFrame(() => { revealQueued = false; revealNote(el); });
  }
}

// Morphs replace the drawer contents after the add; re-attach the note to the fresh markup.
new MutationObserver(() => pendingNote && placeNote()).observe(document.documentElement, { childList: true, subtree: true });

async function onAdd(button) {
  const row = button.closest('[data-bmp-cu-row]');
  if (!row || button.disabled) return;
  button.disabled = true;
  try {
    const [cart, product] = await Promise.all([
      fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      fetch(`${root()}products/${row.dataset.handle}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
    ]);
    const refSize = refSizeFrom(cart);
    const pick = pickVariant(product, row.dataset.color, refSize);
    if (!pick) { row.remove(); return; }

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart-upsell',
      lines: [{ merchandiseId: String(pick.variant.id), quantity: 1 }],
      promise: deferred.promise,
    }));

    if (pick.fallback) {
      pendingNote = { text: `That size isn't available on this one, so we added ${/^[A-Z]/i.test(String(pick.size)) ? "an" : "a"} ${pick.size}.`, until: Date.now() + NOTE_MS };
    }

    const res = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: pick.variant.id, quantity: 1 }], sections: sectionIds.join(','), sections_url: window.location.pathname }),
    }).then((r) => r.json());

    const fresh = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fresh),
      detail: {
        items: fresh.items,
        source: 'bmp-cart-upsell',
        sections: res.sections,
        didError: Boolean(res.status),
      },
    });
    if (res.status) { pendingNote = null; button.disabled = false; return; }
    placeNote();
    // The new cart line animates in after the morph (height grows without DOM mutations), so re-check a few times.
    if (pendingNote) {
      for (const t of [400, 900, 1500]) {
        setTimeout(() => {
          const el = document.querySelector('#cart-drawer [data-bmp-cu-note]');
          if (pendingNote && el && el.textContent) revealNote(el);
        }, t);
      }
    }
  } catch (e) {
    console.warn('[bmp-cart-upsell]', e);
    button.disabled = false;
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-bmp-cu-add]') : null;
  if (btn) { e.preventDefault(); onAdd(btn); }
});
// BMP-HOOK-START:smart-pairs
// Blend Recently viewed shirts into "Pairs great with". The server renders up to 8 rows in rank order
// (data-src="style" = best sellers of the cart's majority riding style, "top" = overall best sellers; rows 5-8 hidden).
// Here we mix in up to 2 viewed-but-not-added shirts (localStorage 'bmp:rv', written on product pages by
// assets/bmp-recently-viewed.js): style, viewed, style, viewed, then style/overall fill. Never a cart item, no duplicates.
// Re-applied after every drawer morph (cart change). Any failure leaves the server order (overall/style best sellers).
const RV_KEY = 'bmp:rv';
const MAX_VISIBLE = 4;
const MAX_VIEWED = 2;
const productCache = new Map();

function readViewed() {
  try {
    const list = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
  } catch {
    return [];
  }
}

function getProduct(handle) {
  if (!productCache.has(handle)) {
    productCache.set(handle, fetch(`${root()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null));
  }
  return productCache.get(handle);
}

const fileOf = (src) => String(src || '').split('?')[0].split('/').pop();

function featuredColor(product) {
  const ci = product.options.findIndex((o) => (o.name || o) === 'Color');
  if (ci < 0) return '';
  const fi = fileOf(product.featured_image);
  const v = product.variants.find((x) => x.available && x.featured_image && fileOf(x.featured_image.src) === fi)
    || product.variants.find((x) => x.available);
  return v ? v.options[ci] : '';
}

const money = (cents) => `$${(Number(cents) / 100).toFixed(2)}`;
const sized = (src, w) => {
  if (!src) return '';
  const u = String(src).startsWith('//') ? `https:${src}` : String(src);
  return `${u}${u.includes('?') ? '&' : '?'}width=${w}`;
};

function buildRow(product, refSize) {
  const color = featuredColor(product);
  const pick = pickVariant(product, color, refSize);
  if (!pick) return null;
  const v = pick.variant;
  const url = `${root()}products/${product.handle}?variant=${v.id}`;
  const title = String(product.title).replace(/ T-Shirt$/i, '');
  const li = document.createElement('li');
  li.className = 'bmp-cart-upsell__row';
  li.setAttribute('data-bmp-cu-row', '');
  li.dataset.handle = product.handle;
  li.dataset.color = color;
  li.dataset.variantId = String(v.id);
  li.dataset.src = 'viewed';
  const a = document.createElement('a');
  a.className = 'bmp-cart-upsell__media';
  a.href = url;
  a.tabIndex = -1;
  a.setAttribute('aria-hidden', 'true');
  const imgSrc = (v.featured_image && v.featured_image.src) || product.featured_image;
  if (imgSrc) {
    const img = document.createElement('img');
    img.src = sized(imgSrc, 160);
    img.width = 56;
    img.height = 56;
    img.loading = 'lazy';
    img.alt = '';
    a.append(img);
  }
  const info = document.createElement('div');
  info.className = 'bmp-cart-upsell__info';
  const name = document.createElement('a');
  name.className = 'bmp-cart-upsell__name';
  name.href = url;
  name.textContent = title;
  const price = document.createElement('span');
  price.className = 'bmp-cart-upsell__price';
  price.setAttribute('data-bmp-cu-price', '');
  price.textContent = money(v.price);
  info.append(name, price);
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'bmp-cart-upsell__add';
  btn.setAttribute('data-bmp-cu-add', '');
  btn.setAttribute('aria-label', `Add ${product.title}, ${v.title}`);
  btn.textContent = 'Add';
  li.append(a, info, btn);
  return li;
}

const visibleOrder = (track) => [...track.querySelectorAll(':scope > [data-bmp-cu-row]:not([hidden])')].map((r) => r.dataset.handle).join(',');
const applied = new WeakMap(); // box -> { key, order }
const running = new WeakSet();

async function blend(box) {
  const track = box.querySelector('[data-bmp-cu-track]');
  if (!track || running.has(box)) return;
  const viewedAll = readViewed();
  const key = `${box.dataset.sig || ''}|${viewedAll.slice(0, 12).join(',')}`;
  const last = applied.get(box);
  if (last && last.key === key && last.order === visibleOrder(track)) return;
  running.add(box);
  try {
    const inCart = new Set(String(box.dataset.cartHandles || '').split(',').filter(Boolean));
    const rows = [...track.querySelectorAll(':scope > [data-bmp-cu-row]')].filter((r) => !inCart.has(r.dataset.handle));
    const serverRows = rows.filter((r) => r.dataset.src !== 'viewed');
    const byHandle = new Map(rows.map((r) => [r.dataset.handle, r]));
    let refSize = null;
    const viewedRows = [];
    for (const h of viewedAll) {
      if (viewedRows.length >= MAX_VIEWED) break;
      if (inCart.has(h)) continue;
      const existing = byHandle.get(h);
      if (existing && existing.dataset.src !== 'viewed') { viewedRows.push(existing); continue; }
      const product = await getProduct(h);
      if (!product || !product.available) continue;
      if (refSize === null) {
        const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        refSize = refSizeFrom(cart) || '';
      }
      const row = buildRow(product, refSize || null);
      if (row) viewedRows.push(row);
    }
    if (!track.isConnected) return;
    const vSet = new Set(viewedRows.map((r) => r.dataset.handle));
    const style = serverRows.filter((r) => r.dataset.src === 'style' && !vSet.has(r.dataset.handle));
    const top = serverRows.filter((r) => r.dataset.src !== 'style' && !vSet.has(r.dataset.handle));
    const finalRows = [];
    const v = [...viewedRows];
    while (finalRows.length < MAX_VISIBLE && (style.length || v.length)) {
      if (style.length) finalRows.push(style.shift());
      if (finalRows.length < MAX_VISIBLE && v.length) finalRows.push(v.shift());
    }
    while (finalRows.length < MAX_VISIBLE && top.length) finalRows.push(top.shift());
    const spares = [...style, ...top];
    // Drop stale client-built rows from an earlier blend, then lay out.
    track.querySelectorAll(':scope > [data-src="viewed"]').forEach((r) => { if (!finalRows.includes(r)) r.remove(); });
    finalRows.forEach((r) => { r.hidden = false; });
    spares.forEach((r) => { r.hidden = true; });
    track.replaceChildren(...finalRows, ...spares);
    track.classList.toggle('is-single', finalRows.length === 1);
    applied.set(box, { key, order: visibleOrder(track) });
  } catch (e) {
    console.warn('[bmp-cart-upsell] blend', e);
  } finally {
    running.delete(box);
  }
}

let blendQueued = false;
function queueBlend() {
  if (blendQueued) return;
  blendQueued = true;
  requestAnimationFrame(() => {
    blendQueued = false;
    document.querySelectorAll('[data-bmp-cart-upsell]').forEach((box) => { blend(box); });
  });
}
new MutationObserver(queueBlend).observe(document.documentElement, { childList: true, subtree: true });
window.addEventListener('storage', (e) => { if (e.key === RV_KEY) queueBlend(); });
queueBlend();
// BMP-HOOK-END:smart-pairs
// BMP-HOOK-END:cart-upsell
